import { useSeoTags } from "../useSeoTags";
import "../marketMyths.css";
import "../humanArticleTemplate.css";

// WAVE 1, 2026-10-28 -- humanized rewrite of the Corroboration evidence doc,
// same locked template as the 52-week-high pilot. Isolated, unlinked,
// noindexed preview. Numbers re-verified fresh against
// data/corroboration_validation.json before writing (not carried from the
// earlier AI-toned doc): three reads -- Oct-07 prose (low 0.532, high
// 0.533), local dev (low 0.5405, high 0.5367), production (low 0.5299,
// high 0.5400) -- same source file the already-shipped evidence doc cites.

const READS: { label: string; low: number; high: number }[] = [
  { label: "Oct-07 note", low: 0.532, high: 0.533 },
  { label: "Local-dev replay", low: 0.5405, high: 0.5367 },
  { label: "Production (live)", low: 0.5299, high: 0.5400 },
];

function ThreeReadsChart() {
  const width = 640;
  const rowH = 52;
  const height = READS.length * rowH + 36;
  const labelW = 150;
  const plotW = width - labelW - 80;
  const minV = 0.52;
  const maxV = 0.545;
  const scaleX = (v: number) => ((v - minV) / (maxV - minV)) * plotW;

  return (
    <figure className="hatpl-chart">
      <div className="hatpl-chart-legend">
        <span><i style={{ background: "var(--accent)" }} />Low corroboration</span>
        <span><i style={{ background: "#96a0aa" }} />High corroboration</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img"
           aria-label="Hit rate for low vs high corroboration across three independent reads of the same data: the ranking flips between reads">
        {READS.map((r, i) => {
          const y = 10 + i * rowH;
          return (
            <g key={r.label}>
              <text x={0} y={y + 10} className="hatpl-chart-label">{r.label}</text>
              <circle cx={labelW + scaleX(r.low)} cy={y + 2} r={6} fill="var(--accent)" />
              <circle cx={labelW + scaleX(r.high)} cy={y + 20} r={6} fill="#96a0aa" />
              <line x1={labelW + scaleX(r.low)} y1={y + 2} x2={labelW + scaleX(r.high)} y2={y + 20}
                    stroke="var(--rule-strong)" strokeWidth={1.5} />
              <text x={labelW + scaleX(r.low)} y={y - 6} textAnchor="middle" className="hatpl-chart-value">
                {(r.low * 100).toFixed(2)}%
              </text>
              <text x={labelW + scaleX(r.high)} y={y + 38} textAnchor="middle" className="hatpl-chart-value">
                {(r.high * 100).toFixed(2)}%
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption>
        Hit rate for low- vs. high-corroboration predictions, the same comparison measured three separate
        times. Read 1: low edges out high. Read 2 (more data): low still wins, by more. Read 3 (the most
        data, production): the order flips — high now wins. The line connecting each pair's position doesn't
        point the same way twice.
      </figcaption>
      <div className="hatpl-asof">
        As of 2026&#8209;10&#8209;25 · a one-off diagnostic re-run, not on a recompute schedule.
      </div>
    </figure>
  );
}

const CANONICAL = "https://tickerpicker.in/market-myths?evidence=corroboration";

export function CorroborationArticlePage({ onBack }: { onBack: () => void }) {
  useSeoTags({
    title: "We checked whether agreement means anything. It didn't hold up. — Tickerpicker",
    description:
      "Does it help to know several of our models agree on a stock? We measured it three times and got three " +
      "different answers — which is itself the answer.",
    canonical: CANONICAL,
    ogType: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "We checked whether agreement means anything. It didn't hold up.",
      url: CANONICAL,
      publisher: { "@type": "Organization", name: "Tickerpicker" },
      isPartOf: { "@type": "WebPage", name: "Market Myths: Tested", url: "https://tickerpicker.in/market-myths" },
    },
  });

  return (
    <div className="hatpl-shell">
      <nav className="hatpl-nav">
        <a href="/" className="hatpl-brand"><span className="dot" />TickerPicker</a>
        <div className="mm-evid-crumbs">
            <a href="/">Home</a>
            <span className="mm-crumb-sep">/</span>
            <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
          </div>
      </nav>

      <article className="hatpl-article">
        <div className="hatpl-kicker">Market Myths: Tested — corroboration</div>
        <h1>We checked whether agreement means anything. It didn't hold up.</h1>

        <p>
          A few of our stock predictions get built from more than one segment model — a sector model and a
          liquidity-tier model both weighing in on the same name, say. When they agree, does that agreement
          itself mean anything? Not "is the prediction right" — we already check that — but does knowing two
          independent models landed on the same answer make you more confident than one model alone would?
        </p>

        <p>
          It's a reasonable thing to want to be true. Ensembles are supposed to work this way: independent
          opinions, pooled, beat any one opinion on its own. So we measured it — restricted to cases where our
          models already agree on direction, and asked whether the number of genuinely independent sources
          behind that agreement predicts a better hit rate.
        </p>

        <ThreeReadsChart />

        <blockquote className="hatpl-pullquote">
          A real effect doesn't flip sign between re-measurements of the same thing.
        </blockquote>

        <p>
          We ran this three times. Once informally, back in October. Once against a historical replay once more
          data had piled up. Once against the live production database — our biggest, most current sample. The
          first read found almost nothing either way. The second read found low-corroboration predictions doing
          <em> better</em>, which is backwards from the whole premise. The third read — the one with the most
          data — flipped again, with high-corroboration now coming out slightly ahead. Three attempts at
          the same question, three different rankings.
        </p>

        <p>
          That's not a weak positive we should round up. It's the opposite: if corroboration genuinely helped,
          the sign would hold steady as we added more data, even if the size wobbled. It didn't hold steady. We
          don't currently use corroboration count as a confidence signal, and this is why.
        </p>

        <hr className="hatpl-rule" />

        <details className="hatpl-numbers">
          <summary>The full numbers</summary>
          <table>
            <tbody>
              <tr><td>Method</td><td>N_eff (effective independent sources) bucketed low (&lt;1.10) / mid (1.10&ndash;1.30) / high (&gt;1.30), among high-conviction stock-dates only</td></tr>
              <tr><td>Oct-07 note — low / high hit rate</td><td>53.2% / 53.3%</td></tr>
              <tr><td>Local-dev replay — low / mid / high</td><td>54.05% / 52.44% / 53.67%</td></tr>
              <tr><td>Production (live) — low / mid / high</td><td>52.99% / 54.19% / 54.00%</td></tr>
              <tr><td>Local-dev: low&minus;high paired t (by date)</td><td>+1.16</td></tr>
              <tr><td>Local-dev: low&minus;mid paired t (by date)</td><td>+3.17</td></tr>
              <tr><td>Production: low&minus;high paired t (by date)</td><td>&minus;0.77</td></tr>
              <tr><td>Production: low&minus;mid paired t (by date)</td><td>&minus;2.92</td></tr>
              <tr><td>Production scored stock-dates</td><td>289,065</td></tr>
              <tr><td>Production date range</td><td>2026-03-02 to 2026-10-23</td></tr>
            </tbody>
          </table>
          <p className="hatpl-source">
            This is an exploratory diagnostic, not a pre-registered test — no placebo calibration or
            multiple-testing correction behind these numbers.
          </p>
          <p className="hatpl-source">
            Source: <code>.scratch_research/corroboration_validation.py</code> (read-only, no database writes).
            Raw results, all three reads: <code>data/corroboration_validation.json</code>.
          </p>
        </details>

        <div className="mm-evid-footnav">
          <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
        </div>
      </article>
    </div>
  );
}
